import { create } from 'zustand';
import {
  chamberLabelToParam,
  deleteO2kTransition,
  listO2kTransitions,
  postO2kTransition,
  putO2kTransition,
} from '../services/o2k.js';

export const useFileDataStore = create((set, get) => ({
  // Chamber-specific file data for plotting
  chamberAData: null,
  chamberBData: null,
  // User-created transitions per chamber (used by O2 kinetics)
  transitionsA: [], // [{ id, name, start: Date, end: Date, n_points? }]
  transitionsB: [],
  // Lock state per chamber (used by O2 kinetics UI)
  transitionsLockedA: false,
  transitionsLockedB: false,
  // Selection per chamber (for O2 kinetics editor & list)
  selectedTransitionAId: null,
  selectedTransitionBId: null,
  // Transitions loading flags per chamber
  transitionsLoadingA: false,
  transitionsLoadingB: false,
  // O2Kinetics errors per chamber (separate from global error)
  o2kineticsErrorA: null,
  o2kineticsErrorB: null,
  // Marks per chamber (filtered by signal type - O2 only for now)
  chamberAMarks: [],
  chamberBMarks: [],
  // Events per chamber (single time point events)
  chamberAEvents: [],
  chamberBEvents: [],
  // Plot config per chamber (y-axis scales from dld8 file)
  chamberAPlotConfig: [],
  chamberBPlotConfig: [],

  // File metadata
  currentFileMeta: null,

  recentFiles: [],
  uploads: [], // { tempId, fileName, progress, status, serverId? }

  // Loading states
  isLoading: false,
  error: null,

  // Actions
  setChamberData: (chamberAData, chamberBData, fileMeta) =>
    set({
      chamberAData,
      chamberBData,
      currentFileMeta: fileMeta,
      error: null,
    }),

  setChamberMarks: (chamber, marks) =>
    set((state) => {
      // Filter marks by signal type - only show O2 marks for now
      // mark_signal_type can be 'O2', 'O2Flux', 'Time', or null
      // We only show O2 marks (or marks with null signal type, treating them as O2)
      const filteredMarks = Array.isArray(marks)
        ? marks.filter((m) => !m.mark_signal_type || m.mark_signal_type === 'O2Flux')
        : [];

      if (chamber === 'A') {
        return { chamberAMarks: filteredMarks };
      } else if (chamber === 'B') {
        return { chamberBMarks: filteredMarks };
      }
      return state;
    }),

  setChamberEvents: (chamber, events) =>
    set((state) => {
      if (chamber === 'A') {
        return { chamberAEvents: Array.isArray(events) ? events : [] };
      } else if (chamber === 'B') {
        return { chamberBEvents: Array.isArray(events) ? events : [] };
      }
      return state;
    }),

  setChamberPlotConfig: (chamber, plotConfig) => {
    const configArray = Array.isArray(plotConfig) ? plotConfig : [];
    set((state) => {
      if (chamber === 'A') {
        return { chamberAPlotConfig: configArray };
      } else if (chamber === 'B') {
        return { chamberBPlotConfig: configArray };
      }
      return state;
    });
  },

  // Cache recent files in store
  setRecentFiles: (files) => set({ recentFiles: files || [] }),

  // Upload queue helpers
  enqueueUploads: (files) =>
    set((state) => ({
      uploads: [
        ...state.uploads,
        ...Array.from(files || []).map((f) => ({
          tempId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          fileName: f.name,
          progress: 0,
          status: 'queued',
          file: f,
        })),
      ],
    })),
  updateUploadProgress: (tempId, progress) =>
    set((state) => ({
      uploads: state.uploads.map((u) =>
        u.tempId === tempId ? { ...u, progress, status: 'uploading' } : u,
      ),
    })),
  markUploadDone: (tempId, serverId) =>
    set((state) => ({
      uploads: state.uploads.map((u) =>
        u.tempId === tempId ? { ...u, progress: 100, status: 'done', serverId } : u,
      ),
    })),
  markUploadError: (tempId, error) =>
    set((state) => ({
      uploads: state.uploads.map((u) =>
        u.tempId === tempId ? { ...u, status: 'error', error } : u,
      ),
    })),
  cancelUpload: (tempId) =>
    set((state) => ({
      uploads: state.uploads.map((u) => (u.tempId === tempId ? { ...u, status: 'cancelled' } : u)),
    })),
  removeUpload: (tempId) =>
    set((state) => ({
      uploads: state.uploads.filter((u) => u.tempId !== tempId),
    })),

  setLoading: (loading) => set({ isLoading: loading }),

  setError: (error) => set((state) => ({ error, isLoading: error ? false : state.isLoading })),

  // O2Kinetics-specific error handling (doesn't affect Graph.jsx)
  setO2kineticsError: (chamber, error) =>
    set((state) => {
      if (chamber === 'A') {
        return { o2kineticsErrorA: error };
      } else if (chamber === 'B') {
        return { o2kineticsErrorB: error };
      }
      return state;
    }),

  clearO2kineticsError: (chamber) =>
    set((state) => {
      if (chamber === 'A') {
        return { o2kineticsErrorA: null };
      } else if (chamber === 'B') {
        return { o2kineticsErrorB: null };
      }
      return state;
    }),

  clearData: () =>
    set({
      chamberAData: null,
      chamberBData: null,
      chamberAMarks: [],
      chamberBMarks: [],
      chamberAEvents: [],
      chamberBEvents: [],
      chamberAPlotConfig: [],
      chamberBPlotConfig: [],
      transitionsA: [],
      transitionsB: [],
      transitionsLockedA: false,
      transitionsLockedB: false,
      currentFileMeta: null,
      error: null,
      isLoading: false,
    }),

  // Helper to transform API response to PlotPanel format
  transformDld8Data: (apiResponse, fileMeta = null) => {
    if (!apiResponse?.data_series) return null;

    const timeSeriesData = apiResponse.data_series.find((series) => series.type === 'Time');
    const oxygenSeriesData = apiResponse.data_series.find((series) => series.type === 'O2');
    const fluxSeriesData = apiResponse.data_series.find((series) => series.type === 'O2Flux');

    if (!timeSeriesData || !oxygenSeriesData) {
      console.warn('Missing Time or O2 series in API response');
      return null;
    }

    const result = {
      timeSeries: timeSeriesData.data,
      oxygenSeries: oxygenSeriesData.data,
      dataFrequency: fileMeta?.data_frequency || 0.1,
      fileName: fileMeta?.file_name || 'Unknown File',
    };

    // Include flux series if available
    if (fluxSeriesData) {
      result.fluxSeries = fluxSeriesData.data;
    }

    return result;
  },

  // Transitions actions
  setTransitionsLoading: (chamber, loading) =>
    set((state) => {
      if (chamber === 'A') return { transitionsLoadingA: !!loading };
      if (chamber === 'B') return { transitionsLoadingB: !!loading };
      return state;
    }),
  setTransitions: (chamber, transitions) =>
    set((state) => {
      const safe = Array.isArray(transitions) ? transitions : [];
      if (chamber === 'A') return { transitionsA: safe };
      if (chamber === 'B') return { transitionsB: safe };
      return state;
    }),

  // Map backend → store transition
  mapBackendTransitionToStore: (t) => {
    try {
      const m = t?.transition_mark || {};
      const start = new Date((m.time_start ?? 0) * 1000);
      const end = new Date((m.time_end ?? m.time_start ?? 0) * 1000);
      return {
        id: m.id,
        name: m.name || 'transition',
        start,
        end,
        n_points: m.n_points ?? undefined,
        status: 'remote',
        isDirty: false,
        fitParameter: t?.fit_parameter ? { ...t.fit_parameter } : undefined,
        result: t?.result ? { ...t.result } : null,
      };
    } catch {
      return null;
    }
  },

  // Map store → backend transition payload
  mapStoreTransitionToBackend: (tr) => {
    const startMs = (tr.start instanceof Date ? tr.start : new Date(tr.start)).getTime();
    const endMs = (tr.end instanceof Date ? tr.end : new Date(tr.end)).getTime();
    const time_start = Math.min(startMs, endMs) / 1000;
    const time_end = Math.max(startMs, endMs) / 1000;
    return {
      transition_mark: {
        id: tr.id,
        mark_type: 'TIME_RANGE',
        mark_signal_type: null,
        name: tr.name || 'transition',
        time_start,
        time_end,
        n_points: typeof tr.n_points === 'number' ? tr.n_points : 0,
      },
      fit_parameter: tr.fitParameter,
      result: tr.result ?? undefined,
    };
  },

  // Merge/replace transitions with server-provided list (preserve local-only extras when ids collide)
  mergeTransitionsFromServer: (chamber, list, justSavedId = null) =>
    set((state) => {
      const mapFn = get().mapBackendTransitionToStore;
      const incoming = (Array.isArray(list) ? list : []).map(mapFn).filter(Boolean);
      const key = chamber === 'A' ? 'transitionsA' : 'transitionsB';
      const existing = state[key] || [];
      const byId = new Map(existing.map((t) => [t.id, t]));
      for (const remote of incoming) {
        const prev = byId.get(remote.id);
        if (prev && prev.isDirty === true && remote.id !== justSavedId) {
          // Preserve local edits for remote items marked dirty, except for the one just saved
          byId.set(remote.id, {
            ...prev,
            status: 'remote',
            // keep isDirty true to reflect unsaved local changes
            isDirty: true,
          });
        } else {
          // No local edits OR this is the one we just saved → accept server update
          byId.set(remote.id, { ...(prev || {}), ...remote, status: 'remote', isDirty: false });
        }
      }
      return { [key]: Array.from(byId.values()) };
    }),

  // Async: fetch transitions for a chamber (requires currentFileMeta.id)
  fetchTransitionsForChamber: async (chamber) => {
    const { currentFileMeta, setError, mergeTransitionsFromServer, setTransitionsLoading } = get();
    try {
      if (!currentFileMeta?.id) return;
      setTransitionsLoading(chamber, true);
      const chamberParam = chamberLabelToParam(chamber);
      const list = await listO2kTransitions(currentFileMeta.id, chamberParam);
      mergeTransitionsFromServer(chamber, list);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setError?.(detail || 'Failed to load transitions');
    } finally {
      try {
        setTransitionsLoading(chamber, false);
      } catch {}
    }
  },

  // Async: fit selected transition via POST/PUT depending on status
  fitSelectedTransition: async (chamber) => {
    const {
      currentFileMeta,
      selectedTransitionAId,
      selectedTransitionBId,
      transitionsA,
      transitionsB,
      mapStoreTransitionToBackend,
      computeTransitionPoints,
      updateTransition,
      replaceTransitionId,
      mergeTransitionsFromServer,
      setSelectedTransition,
      setO2kineticsError,
      clearO2kineticsError,
      setTransitionsLoading,
    } = get();
    // Clear any previous error when starting a new fit
    clearO2kineticsError(chamber);
    try {
      if (!currentFileMeta?.id) return;
      const id = chamber === 'A' ? selectedTransitionAId : selectedTransitionBId;
      const list = chamber === 'A' ? transitionsA : transitionsB;
      const tr = (Array.isArray(list) ? list : []).find((t) => t.id === id);
      if (!tr) return;
      // Ensure n_points is up-to-date
      const npts = computeTransitionPoints(chamber, tr.start, tr.end);
      // Ensure required fit parameters are present by applying defaults
      const defaultFit = {
        fit_method: 'SHIFTED_HYPERBOLIC_FUNCTION',
        tau: 2.3,
        upper_bound: 10,
        zero_flux_thresh: 3.5,
        outlier_thresh_sigma: 5.9,
        smoothing_points: 200,
        smoothing_poly_order: 1,
      };
      const effectiveFit = { ...defaultFit, ...(tr.fitParameter || {}) };
      // Coerce numeric types
      effectiveFit.tau = Number(effectiveFit.tau);
      effectiveFit.upper_bound = Number(effectiveFit.upper_bound);
      effectiveFit.zero_flux_thresh = Number(effectiveFit.zero_flux_thresh);
      effectiveFit.outlier_thresh_sigma = Number(effectiveFit.outlier_thresh_sigma);
      effectiveFit.smoothing_points = parseInt(effectiveFit.smoothing_points, 10);
      effectiveFit.smoothing_poly_order = parseInt(effectiveFit.smoothing_poly_order, 10);

      const payload = mapStoreTransitionToBackend({
        ...tr,
        n_points: npts,
        fitParameter: effectiveFit,
      });
      const chamberParam = chamberLabelToParam(chamber);
      let res;
      const isRemote = tr.status === 'remote';
      res = isRemote
        ? await putO2kTransition(currentFileMeta.id, chamberParam, payload)
        : await postO2kTransition(currentFileMeta.id, chamberParam, payload);
      // Map response and merge into store entry
      const mapped = get().mapBackendTransitionToStore(res);
      if (mapped) {
        // Store the ID that was just saved
        const savedId = mapped.id || tr.id;
        updateTransition(chamber, tr.id, {
          name: mapped.name ?? tr.name,
          start: mapped.start ?? tr.start,
          end: mapped.end ?? tr.end,
          n_points: typeof mapped.n_points === 'number' ? mapped.n_points : npts,
          fitParameter: mapped.fitParameter ?? tr.fitParameter,
          result: mapped.result ?? null,
          status: 'remote',
          isDirty: false,
        });
        // If created via POST, the server will assign a new UUID: replace local id
        if (!isRemote && mapped.id && mapped.id !== tr.id) {
          replaceTransitionId(chamber, tr.id, mapped.id);
        }
        // Refresh from server to sync latest state, but mark the just-saved transition as clean
        try {
          setTransitionsLoading(chamber, true);
        } catch {}
        const list = await listO2kTransitions(currentFileMeta.id, chamberParam);
        mergeTransitionsFromServer(chamber, list, savedId);
        // Keep current selection pointing to the saved transition id
        try {
          setSelectedTransition(chamber, savedId);
        } catch {}
        try {
          setTransitionsLoading(chamber, false);
        } catch {}
        // Clear any previous error on successful fit
        clearO2kineticsError(chamber);
      }
    } catch (err) {
      // Extract error message - handle both string and array of validation errors
      let errorMessage = 'Fit failed';
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Format Pydantic validation errors
        errorMessage = detail
          .map(
            (e) => `${Array.isArray(e.loc) ? e.loc.join('.') : ''}: ${e.msg || 'Validation error'}`,
          )
          .join('; ');
      } else if (err?.message) {
        errorMessage = err.message;
      }
      setO2kineticsError?.(chamber, errorMessage);
      try {
        setTransitionsLoading(chamber, false);
      } catch {}
      // Don't throw - let the UI handle the error display
    }
  },

  // Async: delete transition (local vs remote aware)
  deleteTransitionManaged: async (chamber, id) => {
    const {
      currentFileMeta,
      transitionsA,
      transitionsB,
      setError,
      removeTransition,
      fetchTransitionsForChamber,
    } = get();
    try {
      const list = chamber === 'A' ? transitionsA : transitionsB;
      const tr = (Array.isArray(list) ? list : []).find((t) => t.id === id);
      if (!tr) return;
      if (tr.status === 'remote') {
        const chamberParam = chamberLabelToParam(chamber);
        await deleteO2kTransition(currentFileMeta.id, chamberParam, id);
      }
      removeTransition(chamber, id);
      // Ensure local state matches server by refetching
      await fetchTransitionsForChamber(chamber);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setError?.(detail || 'Delete failed');
      throw err;
    }
  },
  setTransitionsLocked: (chamber, locked) =>
    set((state) => {
      if (chamber === 'A') return { transitionsLockedA: !!locked };
      if (chamber === 'B') return { transitionsLockedB: !!locked };
      return state;
    }),
  setSelectedTransition: (chamber, id) =>
    set((state) => {
      if (chamber === 'A') return { selectedTransitionAId: id };
      if (chamber === 'B') return { selectedTransitionBId: id };
      return state;
    }),
  addTransition: (chamber, transition) =>
    set((state) => {
      const { computeTransitionPoints } = get();
      const withPoints =
        transition && transition.start && transition.end
          ? {
              ...transition,
              n_points: computeTransitionPoints(chamber, transition.start, transition.end),
            }
          : transition;
      // Mark newly added transitions as local unless explicitly set
      const finalT =
        withPoints && !withPoints.status
          ? { ...withPoints, status: 'local', isDirty: true }
          : { ...withPoints, isDirty: true };
      if (chamber === 'A') return { transitionsA: [...state.transitionsA, finalT] };
      if (chamber === 'B') return { transitionsB: [...state.transitionsB, finalT] };
      return state;
    }),
  // Replace a transition's id (e.g., after server assigns canonical id on POST)
  replaceTransitionId: (chamber, oldId, newId) =>
    set((state) => {
      const key = chamber === 'A' ? 'transitionsA' : 'transitionsB';
      const list = state[key] || [];
      const next = list.map((t) => (t.id === oldId ? { ...t, id: newId, status: 'remote' } : t));
      const selectedKey = chamber === 'A' ? 'selectedTransitionAId' : 'selectedTransitionBId';
      const selectedId = state[selectedKey];
      return { [key]: next, [selectedKey]: selectedId === oldId ? newId : selectedId };
    }),
  // Remove all local (unsaved) transitions for a chamber
  clearLocalTransitions: (chamber) =>
    set((state) => {
      const key = chamber === 'A' ? 'transitionsA' : 'transitionsB';
      const list = state[key] || [];
      return { [key]: list.filter((t) => t.status === 'remote') };
    }),
  updateTransition: (chamber, id, updater) =>
    set((state) => {
      const { computeTransitionPoints } = get();
      const mapFn = (t) => {
        if (t.id !== id) return t;
        const next = typeof updater === 'function' ? updater(t) : { ...t, ...updater };
        if (next?.start && next?.end) {
          next.n_points = computeTransitionPoints(chamber, next.start, next.end);
        }
        // Mark dirty on user edits if this transition is already remote
        if (t.status === 'remote') {
          next.isDirty = true;
        }
        return next;
      };
      if (chamber === 'A') return { transitionsA: state.transitionsA.map(mapFn) };
      if (chamber === 'B') return { transitionsB: state.transitionsB.map(mapFn) };
      return state;
    }),
  // Set or merge fit parameters for a transition
  setFitParameters: (chamber, id, params) =>
    set((state) => {
      const key = chamber === 'A' ? 'transitionsA' : 'transitionsB';
      const list = state[key] || [];
      const next = list.map((t) => {
        if (t.id !== id) return t;
        const current = t.fitParameter || {};
        const updated = { ...t, fitParameter: { ...current, ...(params || {}) } };
        if (t.status === 'remote') updated.isDirty = true;
        return updated;
      });
      return { [key]: next };
    }),
  removeTransition: (chamber, id) =>
    set((state) => {
      if (chamber === 'A')
        return {
          transitionsA: state.transitionsA.filter((t) => t.id !== id),
          selectedTransitionAId:
            state.selectedTransitionAId === id ? null : state.selectedTransitionAId,
        };
      if (chamber === 'B')
        return {
          transitionsB: state.transitionsB.filter((t) => t.id !== id),
          selectedTransitionBId:
            state.selectedTransitionBId === id ? null : state.selectedTransitionBId,
        };
      return state;
    }),
  computeTransitionPoints: (chamber, start, end) => {
    const { chamberAData, chamberBData } = get();
    const data = chamber === 'A' ? chamberAData : chamberBData;
    if (!data?.timeSeries || !Array.isArray(data.timeSeries)) return undefined;
    try {
      const startSec = start instanceof Date ? start.getTime() / 1000 : Number(start);
      const endSec = end instanceof Date ? end.getTime() / 1000 : Number(end);
      if (!Number.isFinite(startSec) || !Number.isFinite(endSec)) return undefined;
      const lo = Math.min(startSec, endSec);
      const hi = Math.max(startSec, endSec);
      let count = 0;
      for (let i = 0; i < data.timeSeries.length; i++) {
        const t = data.timeSeries[i];
        if (t >= lo && t <= hi) count++;
      }
      return count;
    } catch {
      return undefined;
    }
  },
}));
